/**
 * NotificationsList — popover content for the bell. Maps each `type` to
 * copy + deep-link; unknown types get a generic title and stay
 * dismissable. Purely presentational — NotificationsBell owns state and
 * mark-as-read.
 */

import { Box, Button, IconButton, List, ListItemButton, Typography, alpha } from '@mui/material';
import {
  CalendarMonth, ConfirmationNumber, EventAvailable, LocalOffer,
  Campaign, Notifications, Close as CloseIcon,
} from '@mui/icons-material';
import { colors, designTokens } from '../../theme/theme';
import { timeAgo } from '../../utils/timeAgo';

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  created_at: string;
  is_read: boolean;
}

interface NotificationsListProps {
  items: NotificationItem[];
  onNavigate: (path: string) => void;
  onMarkRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onSeeAll: () => void;
}

interface TypeMeta {
  /** Path to navigate on click. `null` → no deep-link (still mark-read on click). */
  buildPath: (item: NotificationItem) => string | null;
  /** Icon shown on the left rail. */
  icon: React.ReactNode;
  /** Whether this type is dismissable inline (X button). */
  dismissable: boolean;
}

// Type → behavior. Keys mirror backend NotificationType enum names. Unknown
// types fall through to the default at the end.
const TYPE_MAP: Record<string, TypeMeta> = {
  voucher_unlock: {
    buildPath: () => '/vouchers',
    icon: <ConfirmationNumber sx={{ fontSize: 20, color: colors.primary.main }} />,
    dismissable: false,
  },
  event_reschedule: {
    buildPath: (item) => {
      const eventId = typeof item.data?.event_id === 'string' ? item.data.event_id : null;
      return eventId ? `/events/${eventId}` : '/events';
    },
    icon: <CalendarMonth sx={{ fontSize: 20, color: colors.warning.main }} />,
    dismissable: false,
  },
  event_rsvp_confirmed: {
    buildPath: (item) => {
      const eventId = typeof item.data?.event_id === 'string' ? item.data.event_id : null;
      return eventId ? `/events/${eventId}` : '/events';
    },
    icon: <CalendarMonth sx={{ fontSize: 20, color: colors.success.main }} />,
    dismissable: false,
  },
  attendance_otp: {
    buildPath: () => '/events/upcoming',
    icon: <EventAvailable sx={{ fontSize: 20, color: colors.accent.main }} />,
    dismissable: false,
  },
  partner_deal: {
    buildPath: (item) => {
      const slug = typeof item.data?.slug === 'string' ? item.data.slug : null;
      return slug ? `/partner/${slug}` : null;
    },
    icon: <LocalOffer sx={{ fontSize: 20, color: colors.secondary.main }} />,
    dismissable: false,
  },
  admin_announcement: {
    buildPath: () => null,
    icon: <Campaign sx={{ fontSize: 20, color: colors.info.main }} />,
    dismissable: true,
  },
};

const DEFAULT_META: TypeMeta = {
  buildPath: () => null,
  icon: <Notifications sx={{ fontSize: 20, color: colors.neutral[500] }} />,
  dismissable: true,
};

export default function NotificationsList({
  items, onNavigate, onMarkRead, onDismiss, onSeeAll,
}: NotificationsListProps) {
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${designTokens.glass.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: '0.95rem',
            color: '#FFFFFF',
          }}
        >
          Notifications
        </Typography>
        <Button
          size="small"
          onClick={onSeeAll}
          sx={{
            color: colors.primary.main,
            fontSize: '0.78rem',
            textTransform: 'none',
            minWidth: 'auto',
            px: 1,
            '&:hover': { bgcolor: alpha(colors.primary.main, 0.08) },
          }}
        >
          See all
        </Button>
      </Box>

      {/* Empty state */}
      {items.length === 0 && (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Notifications sx={{ fontSize: 36, color: colors.neutral[400], mb: 1 }} />
          <Typography
            variant="body2"
            sx={{ color: colors.neutral[500], fontSize: '0.85rem' }}
          >
            You're all caught up.
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.neutral[400], fontSize: '0.7rem', mt: 0.5, display: 'block' }}
          >
            New notifications will appear here in real time.
          </Typography>
        </Box>
      )}

      {/* List */}
      {items.length > 0 && (
        <List disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {items.map((item) => {
            const meta = TYPE_MAP[item.type] || DEFAULT_META;
            const path = meta.buildPath(item);

            const handleClick = () => {
              if (!item.is_read) onMarkRead(item.id);
              if (path) onNavigate(path);
            };

            return (
              <ListItemButton
                key={item.id}
                onClick={handleClick}
                sx={{
                  px: 2,
                  py: 1.25,
                  alignItems: 'flex-start',
                  gap: 1.5,
                  borderBottom: `1px solid ${designTokens.glass.border}`,
                  bgcolor: item.is_read ? 'transparent' : alpha(colors.primary.main, 0.05),
                  transition: designTokens.transition.fast,
                  '&:hover': {
                    bgcolor: alpha(colors.primary.main, 0.08),
                  },
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                {/* Icon rail */}
                <Box sx={{ pt: 0.25, flexShrink: 0 }}>
                  {meta.icon}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, justifyContent: 'space-between' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: item.is_read ? 500 : 700,
                        color: '#FFFFFF',
                        fontSize: '0.85rem',
                        lineHeight: 1.35,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.neutral[400],
                        fontSize: '0.65rem',
                        flexShrink: 0,
                      }}
                    >
                      {timeAgo(item.created_at)}
                    </Typography>
                  </Box>
                  {item.body && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.neutral[500],
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {item.body}
                    </Typography>
                  )}
                </Box>

                {/* Inline dismiss for dismissable types */}
                {meta.dismissable && (
                  <IconButton
                    aria-label="Dismiss notification"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(item.id);
                    }}
                    sx={{
                      flexShrink: 0,
                      color: colors.neutral[400],
                      '&:hover': { color: colors.error.main, bgcolor: 'transparent' },
                      width: 24,
                      height: 24,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}
